import React, { createContext, useContext, useState, useCallback } from 'react';
import { TSHIRT_COLORS } from '../config/productImages';

const EditorContext = createContext(null);

const TSHIRT_COLOR_HEX = TSHIRT_COLORS.map(c => c.hex);
const TSHIRT_SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL"];
const TSHIRT_PRICE = 22.99;

const initialState = {
  color: '#FFFFFF',
  size: 'M',
  view: 'front', // front, back, left, right
  // Separate elements for each view
  frontElements: [],
  backElements: [],
  leftElements: [],
  rightElements: [],
  selectedElementId: null,
};

export function EditorProvider({ children }) {
  const [state, setState] = useState(initialState);
  
  const setColor = useCallback((color) => {
    setState(prev => ({ ...prev, color }));
  }, []);
  
  const setSize = useCallback((size) => {
    setState(prev => ({ ...prev, size }));
  }, []);
  
  const setView = useCallback((view) => {
    setState(prev => ({ ...prev, view, selectedElementId: null }));
  }, []);
  
  // Add element to current view
  const addElement = useCallback((element) => {
    const id = `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setState(prev => {
      const elementsKey = `${prev.view}Elements`;
      return {
        ...prev,
        [elementsKey]: [...prev[elementsKey], { ...element, id }],
        selectedElementId: id,
      };
    });
    return id;
  }, []);
  
  const updateElement = useCallback((id, updates) => {
    setState(prev => {
      const updateElements = (elements) =>
        elements.map(el => el.id === id ? { ...el, ...updates } : el);
      
      return {
        ...prev,
        frontElements: updateElements(prev.frontElements),
        backElements: updateElements(prev.backElements),
        leftElements: updateElements(prev.leftElements),
        rightElements: updateElements(prev.rightElements),
      };
    });
  }, []);
  
  const removeElement = useCallback((id) => {
    setState(prev => ({
      ...prev,
      frontElements: prev.frontElements.filter(el => el.id !== id),
      backElements: prev.backElements.filter(el => el.id !== id),
      leftElements: prev.leftElements.filter(el => el.id !== id),
      rightElements: prev.rightElements.filter(el => el.id !== id),
      selectedElementId: prev.selectedElementId === id ? null : prev.selectedElementId,
    }));
  }, []);
  
  const setSelectedElementId = useCallback((id) => {
    setState(prev => ({ ...prev, selectedElementId: id }));
  }, []);
  
  const clearSelection = useCallback(() => {
    setState(prev => ({ ...prev, selectedElementId: null }));
  }, []);
  
  // Clear all elements for the current view
  const clearAllElements = useCallback(() => {
    setState(prev => ({
      ...prev,
      [`${prev.view}Elements`]: [],
      selectedElementId: null,
    }));
  }, []);
  
  // Get elements for current view
  const getCurrentElements = useCallback(() => {
    const elementsKey = `${state.view}Elements`;
    return state[elementsKey] || [];
  }, [state]);
  
  // Get elements for a specific view (used by canvases that render all views)
  const getElementsForView = useCallback((viewName) => {
    const elementsKey = `${viewName}Elements`;
    return state[elementsKey] || [];
  }, [state]);
  
  // Check if any view has designs
  const hasAnyDesign = useCallback(() => {
    return state.frontElements.length > 0 || 
           state.backElements.length > 0 || 
           state.leftElements.length > 0 || 
           state.rightElements.length > 0;
  }, [state.frontElements, state.backElements, state.leftElements, state.rightElements]);
  
  // Get all designs by view
  const getAllDesigns = useCallback(() => {
    return {
      front: state.frontElements,
      back: state.backElements,
      left: state.leftElements,
      right: state.rightElements,
    };
  }, [state.frontElements, state.backElements, state.leftElements, state.rightElements]);
  
  const resetEditor = useCallback(() => {
    setState(initialState);
  }, []);
  
  // Load designs from a cart item (for editing)
  const loadDesigns = useCallback((designs, itemColor, itemSize) => {
    setState(prev => ({
      ...prev,
      color: itemColor || prev.color,
      size: itemSize || prev.size,
      view: 'front',
      frontElements: designs.front || [],
      backElements: designs.back || [],
      leftElements: designs.left || [],
      rightElements: designs.right || [],
      selectedElementId: null,
    }));
  }, []);
  
  const value = {
    ...state,
    setColor,
    setSize,
    setView,
    addElement,
    updateElement,
    removeElement,
    setSelectedElementId,
    clearSelection,
    clearAllElements,
    getCurrentElements,
    getElementsForView,
    hasAnyDesign,
    getAllDesigns,
    resetEditor,
    loadDesigns,
    TSHIRT_COLORS: TSHIRT_COLOR_HEX,
    TSHIRT_SIZES,
    TSHIRT_PRICE,
  };
  
  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
}

export function useEditor() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
}

export { TSHIRT_COLOR_HEX as TSHIRT_COLORS, TSHIRT_SIZES, TSHIRT_PRICE };
